/**
 * Encounter Section Template API client functions
 */

import { API_BASE_URL } from '../config/api';
import {
  EncounterSectionTemplate,
  CreateTemplateRequest,
  UpdateTemplateRequest,
  ReorderQuestionsRequest,
} from '../types/encounterTemplate';

const getAuthToken = (): string | null => {
  return localStorage.getItem('access_token') || localStorage.getItem('token');
};

const getAuthHeaders = () => {
  const token = getAuthToken();
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
  };
};

/**
 * Get all templates for a section (standard + practice-specific)
 */
export const getTemplatesForSection = async (
  practiceId: string,
  sectionName: string
): Promise<EncounterSectionTemplate[]> => {
  const response = await fetch(
    `${API_BASE_URL}/api/v1/practices/${practiceId}/encounter-templates/${sectionName}`,
    {
      headers: getAuthHeaders(),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to fetch templates');
  }

  return response.json();
};

/**
 * Create a new custom question for a practice
 */
export const createCustomQuestion = async (
  practiceId: string,
  sectionName: string,
  templateData: CreateTemplateRequest
): Promise<EncounterSectionTemplate> => {
  const response = await fetch(
    `${API_BASE_URL}/api/v1/practices/${practiceId}/encounter-templates/${sectionName}`,
    {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(templateData),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to create custom question');
  }

  return response.json();
};

/**
 * Update a custom question
 */
export const updateCustomQuestion = async (
  templateId: string,
  templateData: UpdateTemplateRequest
): Promise<EncounterSectionTemplate> => {
  const response = await fetch(
    `${API_BASE_URL}/api/v1/encounter-templates/${templateId}`,
    {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify(templateData),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to update custom question');
  }

  return response.json();
};

/**
 * Delete a custom question
 */
export const deleteCustomQuestion = async (templateId: string): Promise<void> => {
  const response = await fetch(
    `${API_BASE_URL}/api/v1/encounter-templates/${templateId}`,
    {
      method: 'DELETE',
      headers: getAuthHeaders(),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to delete custom question');
  }
};

/**
 * Reorder questions for a section
 */
export const reorderQuestions = async (
  practiceId: string,
  sectionName: string,
  questionIds: string[]
): Promise<EncounterSectionTemplate[]> => {
  const response = await fetch(
    `${API_BASE_URL}/api/v1/practices/${practiceId}/encounter-templates/${sectionName}/reorder`,
    {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ question_ids: questionIds } as ReorderQuestionsRequest),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to reorder questions');
  }

  return response.json();
};

// ============================================================================
// Standard Question Exclusion API
// ============================================================================

/**
 * Exclude (hide) a standard question for a practice.
 * This allows practices to hide standard questions they don't want to use.
 * Other practices are unaffected.
 */
export const excludeStandardQuestion = async (
  practiceId: string,
  sectionName: string,
  templateId: string
): Promise<{ message: string }> => {
  const response = await fetch(
    `${API_BASE_URL}/api/v1/practices/${practiceId}/encounter-templates/${sectionName}/exclude/${templateId}`,
    {
      method: 'POST',
      headers: getAuthHeaders(),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to exclude question');
  }

  return response.json();
};

/**
 * Include (unhide) a previously excluded standard question for a practice.
 * This restores the question so it appears in encounter forms again.
 */
export const includeStandardQuestion = async (
  practiceId: string,
  sectionName: string,
  templateId: string
): Promise<{ message: string }> => {
  const response = await fetch(
    `${API_BASE_URL}/api/v1/practices/${practiceId}/encounter-templates/${sectionName}/exclude/${templateId}`,
    {
      method: 'DELETE',
      headers: getAuthHeaders(),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to include question');
  }

  return response.json();
};

/**
 * Get all standard questions that have been excluded by a practice for a section.
 * This is useful for showing a list of hidden questions that can be re-enabled.
 */
export const getExcludedQuestions = async (
  practiceId: string,
  sectionName: string
): Promise<EncounterSectionTemplate[]> => {
  const response = await fetch(
    `${API_BASE_URL}/api/v1/practices/${practiceId}/encounter-templates/${sectionName}/excluded`,
    {
      headers: getAuthHeaders(),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to fetch excluded questions');
  }

  return response.json();
};

